import { BiDatasetCatalog } from './catalog';
import {
  BiDatasetGrantView,
  BiDatasetPermission,
  BiUploadedDatasetView,
} from './dataset';
import { BiDatasetRefKind } from './query';
import { BiQueryResult } from './result';

// Mirror of backend `models/src/bi/library.ts`.

/** The data library's sections (spec §5.5). */
export enum BiDatasetSection {
  /** Datasets the caller uploaded. */
  Mine = 'mine',
  /** Datasets others shared with the caller or the whole store. */
  Shared = 'shared',
  /** The SQL datasets, read-only. */
  Builtin = 'builtin',
}

/** `GET /api/bi/datasets` */
export interface BiListDatasetsRequest {
  section: BiDatasetSection;
  /**
   * Narrows the list to datasets matching it — name, description or field
   * labels — best match first.
   */
  query?: string;
}

/** A SQL dataset with the fields the caller's role may use. */
export interface BiBuiltinDatasetEntry {
  kind: BiDatasetRefKind.Sql;
  catalog: BiDatasetCatalog;
}

/** An uploaded dataset the caller can use (`mine` or `shared`). */
export interface BiUploadedDatasetEntry {
  kind: BiDatasetRefKind.Upload;
  dataset: BiUploadedDatasetView;
}

/** One dataset of a library section. */
export type BiDatasetEntry = BiBuiltinDatasetEntry | BiUploadedDatasetEntry;

/** `GET /api/bi/datasets` response. */
export interface BiDatasetList {
  section: BiDatasetSection;
  datasets: BiDatasetEntry[];
}

/** A dashboard an uploaded dataset's cards sit on, that the caller sees. */
export interface BiDatasetUsageDashboard {
  dashboardId: string;
  name: string;
  cardCount: number;
}

/** `GET /api/bi/datasets/:id/usage`: where the dataset is used. */
export interface BiDatasetUsage {
  /** The dashboards the caller can see, latest edited first. */
  dashboards: BiDatasetUsageDashboard[];
  /** Dashboards using it that the caller cannot see. */
  hiddenDashboardCount: number;
  /** Live cards on every dashboard using it. */
  cardCount: number;
}

/** An uploaded dataset with its fields and where it is used. */
export interface BiUploadedDatasetDetail extends BiUploadedDatasetEntry {
  catalog: BiDatasetCatalog;
  /** The dashboards behind `dataset.usage`'s counts, as far as visible. */
  usageDetail: BiDatasetUsage;
}

/**
 * `GET /api/bi/datasets/:ref` response; `ref` is a built-in model key or an
 * uploaded dataset id.
 */
export type BiDatasetDetail = BiBuiltinDatasetEntry | BiUploadedDatasetDetail;

/** `GET /api/bi/datasets/:id/preview`: the first rows, as stored. */
export type BiDatasetPreview = BiQueryResult;

/** `PATCH /api/bi/datasets/:id`: rename and describe (`manage`). */
export interface BiUpdateDatasetRequest {
  name?: string;
  /** Null clears it. */
  description?: string | null;
}

/** Who a dataset's results would reach: its whole store, or these members. */
export interface BiDatasetAudience {
  /** Every member of the store. */
  everyone: boolean;
  /** The owner and grantees by name, when not everyone. */
  members: { memberId: number; name: string | null }[];
}

export interface BiDatasetGrantInput {
  memberId: number;
  permission: BiDatasetPermission;
}

/**
 * A sharing change: applied, or (a widening without `confirmed`) only the
 * audience it would reach. Either way the audience is the one after it.
 */
export interface BiDatasetSharingResult {
  applied: boolean;
  /** Whether the change lets anyone new use the dataset. */
  widens: boolean;
  audience: BiDatasetAudience;
  dataset: BiUploadedDatasetView;
  grants: BiDatasetGrantView[];
}

export const kBiMaxDatasetName = 100;
export const kBiMaxGrantsPerDataset = 200;

const kDatasetId = /^bids_[0-9a-z]{26}$/;

/** An uploaded dataset id, as opposed to a built-in model key. */
export function isBiDatasetId(value: string): boolean {
  return kDatasetId.test(value);
}
